import React, { useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { normalizeAdminRichText } from "../../../utils/richText";
import excelIcon from "../../../assets/images/ui/descarga_excel.png";
import bannerImg from "../../../assets/images/ui/banner.png";
import {
  normalizeTipoFenomeno,
  compareFenomenoTipos,
  getFenomenoColumns,
  getFenomenoExcelColumnDefs,
} from "./fenomenosAsociadosColumns";
import {
  getFenomenoTipoNormFromLabel,
  getFenomenoTiposConDatos,
  fenomenoBelongsToTipo,
} from "./fenomenosTipoConfig";
import EvtFenomenosTipoTabs from "./EvtFenomenosTipoTabs";
import EvtImagenesGaleriaModal from "./EvtImagenesGaleriaModal";
import { EVENTO_EMPTY_MESSAGES } from "./eventoDetalleConstants";
import "./TablaParametrosEruptivos.css";

function fenColLayoutClass(c) {
  if (!c?.key) return "";
  if (c.key === "__fuente") return "evt-col-fuente";
  if (c.key === "__reporte") return "evt-col-reporte";
  if (c.key === "__imagenes") return "evt-col-img";
  return "evt-col-metric";
}

function rowTipoFenomeno(row) {
  return String(
    row?.tipo_fenomeno_asociado ?? row?.tipo_fenomeno ?? row?.tipo ?? "",
  ).trim();
}

export default function TablaFenomenosAsociados({
  rows,
  volcanTitle,
  eventoTitle,
}) {
  const tiposDisponibles = useMemo(
    () => getFenomenoTiposConDatos(rows),
    [rows],
  );

  const [tipoSel, setTipoSel] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);
  const [excelBusy, setExcelBusy] = useState(false);

  useEffect(() => {
    setTipoSel((prev) => {
      if (tiposDisponibles.some((item) => item.label === prev)) return prev;
      return tiposDisponibles[0]?.label ?? null;
    });
  }, [tiposDisponibles]);

  const tipoNorm = getFenomenoTipoNormFromLabel(tipoSel);
  const cols = useMemo(
    () => (tipoNorm ? getFenomenoColumns(tipoNorm) : []),
    [tipoNorm],
  );

  const filtered = useMemo(() => {
    if (!tipoSel) return [];
    return (rows || []).filter((p) => fenomenoBelongsToTipo(p, tipoSel));
  }, [rows, tipoSel]);

  const openGallery = (imagenes) => {
    setModalImages(Array.isArray(imagenes) ? imagenes : []);
    setModalIndex(0);
    setModalOpen(true);
  };

  const closeGallery = () => {
    setModalOpen(false);
    setModalImages([]);
    setModalIndex(0);
  };

  const nextImg = () => {
    if (modalImages.length <= 1) return;
    setModalIndex((i) => (i === modalImages.length - 1 ? 0 : i + 1));
  };
  const prevImg = () => {
    if (modalImages.length <= 1) return;
    setModalIndex((i) => (i === 0 ? modalImages.length - 1 : i - 1));
  };

  const shouldRenderRich = (val) => {
    if (val == null) return false;
    const s = String(val);
    if (!s.trim()) return false;
    return s.includes("<") || s.includes("&lt;") || s.includes("\n");
  };

  if (!rows || rows.length === 0) {
    return (
      <section className="evt-card evt-card--table-embed evt-param-table-card">
        <h4 className="evt-card-title">Fenómenos asociados</h4>
        <div className="evt-empty">{EVENTO_EMPTY_MESSAGES.fenomenos}</div>
      </section>
    );
  }

  return (
    <>
      <div className="evt-param-outer-layout">
        <section className="evt-card evt-card--table-embed evt-param-table-card">
          <h4 className="evt-card-title">Fenómenos asociados</h4>

          <div className="evt-param-hero-row">
            {tiposDisponibles.length > 0 ? (
              <div
                className={`evt-efectos-tabs-card evt-param-tabs-card${
                  tiposDisponibles.length === 1 ? " is-single-tab" : ""
                }`}
              >
                <EvtFenomenosTipoTabs
                  items={tiposDisponibles}
                  value={tipoSel}
                  onChange={setTipoSel}
                />
              </div>
            ) : null}

            <div className="evt-param-filebox">
              <div className="evt-param-xlsx-card">
                <button
                  type="button"
                  id="evt-fen-excel-trigger"
                  className="evt-param-xlsx-card__icon-btn evt-excel-btn"
                  data-sgc-tooltip="Descargar Excel"
                  aria-label="Descargar Excel"
                  disabled={excelBusy}
                  onClick={async () => {
                    if (excelBusy) return;

                    const htmlToPlainText = (value) => {
                      if (value == null) return "";
                      const s = String(value);
                      if (!s.trim()) return "";
                      if (!/[<>]/.test(s)) return s.trim();
                      try {
                        const doc = new DOMParser().parseFromString(
                          s,
                          "text/html",
                        );
                        return String(doc?.body?.textContent ?? "")
                          .replace(/\s+/g, " ")
                          .trim();
                      } catch {
                        return s
                          .replace(/<[^>]*>/g, " ")
                          .replace(/\s+/g, " ")
                          .trim();
                      }
                    };

                    const safeSheetName = (name, used) => {
                      const base =
                        String(name ?? "")
                          .trim()
                          .replace(new RegExp("[\\[\\]*/\\\\?:]", "g"), " ")
                          .replace(/\s+/g, " ")
                          .trim()
                          .slice(0, 31) || "Fenómenos";
                      if (!used.has(base)) {
                        used.add(base);
                        return base;
                      }
                      for (let i = 2; i < 100; i += 1) {
                        const suffix = ` (${i})`;
                        const candidate = (
                          base.slice(0, 31 - suffix.length) + suffix
                        ).slice(0, 31);
                        if (!used.has(candidate)) {
                          used.add(candidate);
                          return candidate;
                        }
                      }
                      return base.slice(0, 31);
                    };

                    const upper = (s) => String(s ?? "").toUpperCase();

                    const buildWorkbook = async () => {
                      const wb = new ExcelJS.Workbook();
                      wb.creator = "Servicio Geológico Colombiano";
                      wb.created = new Date();

                      const bannerBuf = await fetch(bannerImg).then((r) =>
                        r.arrayBuffer(),
                      );
                      const bannerId = wb.addImage({
                        buffer: bannerBuf,
                        extension: "png",
                      });

                      const byTipo = new Map();
                      (rows || []).forEach((p) => {
                        const t = rowTipoFenomeno(p) || "—";
                        if (!byTipo.has(t)) byTipo.set(t, []);
                        byTipo.get(t).push(p);
                      });

                      const usedNames = new Set();
                      const tiposLocal = Array.from(byTipo.keys()).sort(
                        (a, b) =>
                          compareFenomenoTipos(
                            normalizeTipoFenomeno(a),
                            normalizeTipoFenomeno(b),
                          ),
                      );

                      tiposLocal.forEach((tipo) => {
                        const tipoNormLocal = normalizeTipoFenomeno(tipo);
                        const excelCols =
                          getFenomenoExcelColumnDefs(tipoNormLocal);
                        const sheetCols = [
                          { key: "__id", label: "ID", align: "center" },
                          ...excelCols,
                        ];

                        const sheetName = safeSheetName(tipo, usedNames);
                        const ws = wb.addWorksheet(sheetName, {
                          properties: { defaultRowHeight: 18 },
                          views: [{ state: "frozen", ySplit: 7 }],
                        });

                        sheetCols.forEach((c, idx) => {
                          const col = ws.getColumn(idx + 1);
                          if (c.key === "__id") col.width = 10;
                          else if (c.key === "__fuente") col.width = 26;
                          else if (c.key === "__reporte") col.width = 34;
                          else if (c.align === "justify") col.width = 38;
                          else col.width = 20;
                        });

                        ws.getRow(1).height = 26;
                        ws.getRow(2).height = 26;
                        ws.getRow(3).height = 26;
                        ws.addImage(bannerId, {
                          tl: { col: 0, row: 0 },
                          br: { col: sheetCols.length, row: 3 },
                          editAs: "oneCell",
                        });

                        const lastColLetter = ws.getColumn(
                          sheetCols.length,
                        ).letter;
                        ws.mergeCells(`A4:${lastColLetter}4`);
                        ws.getCell("A4").value =
                          `Reporte fenómenos asociados (Volcán: ${
                            String(volcanTitle ?? "").trim() || "—"
                          } | Evento: ${String(eventoTitle ?? "").trim() || "—"})`;
                        ws.getCell("A4").font = {
                          bold: true,
                          color: { argb: "FF1E4D2B" },
                          size: 11,
                        };
                        ws.getCell("A4").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(4).height = 20;

                        ws.mergeCells(`A5:${lastColLetter}5`);
                        ws.getCell("A5").value = `Fenómeno asociado: ${tipo}`;
                        ws.getCell("A5").font = {
                          bold: true,
                          color: { argb: "FF005833" },
                          size: 10,
                        };
                        ws.getCell("A5").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(5).height = 18;

                        ws.mergeCells(`A6:${lastColLetter}6`);
                        ws.getCell("A6").value =
                          `Generado: ${new Date().toLocaleString("es-CO")}`;
                        ws.getCell("A6").font = {
                          color: { argb: "FF646464" },
                          size: 10,
                        };
                        ws.getCell("A6").alignment = {
                          vertical: "middle",
                          horizontal: "center",
                        };
                        ws.getRow(6).height = 18;

                        const headerRow = ws.addRow(
                          sheetCols.map((c) => upper(c.label)),
                        );
                        headerRow.font = {
                          bold: true,
                          color: { argb: "FFFFFFFF" },
                        };
                        headerRow.alignment = {
                          vertical: "middle",
                          horizontal: "center",
                          wrapText: true,
                        };
                        headerRow.height = 28;
                        headerRow.eachCell((cell) => {
                          cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FF005833" },
                          };
                          cell.border = {
                            top: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            left: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            bottom: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                            right: {
                              style: "thin",
                              color: { argb: "FFBFC9C2" },
                            },
                          };
                        });

                        const list = byTipo.get(tipo) || [];
                        list.forEach((p) => {
                          const detalle = p?.detalle ?? {};
                          const values = sheetCols.map((c) => {
                            if (c.key === "__id") return p?.id ?? "—";
                            if (c.key === "__fuente")
                              return (
                                String(
                                  p?.referencia_bibliografica ?? "—",
                                ).trim() || "—"
                              );
                            if (c.key === "__reporte") {
                              const rep = detalle?.reporte_bibliografico ?? "";
                              return htmlToPlainText(rep) || "—";
                            }
                            const raw = detalle?.[c.key];
                            return (
                              htmlToPlainText(raw) ||
                              String(raw ?? "").trim() ||
                              "—"
                            );
                          });

                          const r = ws.addRow(values);
                          r.alignment = { vertical: "top", wrapText: true };
                          r.eachCell((cell, colNumber) => {
                            const k = sheetCols[colNumber - 1]?.key;
                            const align =
                              k === "__id"
                                ? "center"
                                : sheetCols[colNumber - 1]?.align === "center"
                                  ? "center"
                                  : "left";
                            cell.alignment = {
                              vertical: "top",
                              horizontal: align,
                              wrapText: true,
                            };
                            cell.border = {
                              top: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              left: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              bottom: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                              right: {
                                style: "thin",
                                color: { argb: "FFE0E4E5" },
                              },
                            };
                          });
                        });
                      });

                      return wb;
                    };

                    try {
                      setExcelBusy(true);
                      const wb = await buildWorkbook();
                      const buf = await wb.xlsx.writeBuffer();
                      saveAs(
                        new Blob([buf], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        }),
                        "reporte_fenomenos_asociados.xlsx",
                      );
                    } finally {
                      setExcelBusy(false);
                    }
                  }}
                >
                  <img src={excelIcon} alt="" />
                </button>
                <div className="evt-param-xlsx-card__meta">
                  <div className="evt-param-xlsx-card__title">
                    Archivo completo .xlsx
                  </div>
                  <button
                    type="button"
                    className="evt-param-xlsx-card__link"
                    disabled={excelBusy}
                    aria-label="Ver y descargar archivo Excel completo"
                    onClick={() =>
                      document.getElementById("evt-fen-excel-trigger")?.click()
                    }
                  >
                    Ver y descargar
                    <i className="bi bi-box-arrow-up-right" aria-hidden />
                  </button>
                </div>
              </div>

              <div className="evt-param-filebox__divider" aria-hidden="true" />

              <div className="evt-param-filebox__note" role="status">
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                <span>
                  Este archivo contiene información detallada (fenómenos,
                  fuentes y métricas completas) que no se muestra en esta tabla.
                </span>
              </div>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="tabla-container">
              <table className="tabla-documentos tabla-documentos--parametros tabla-documentos--fenomenos">
                <colgroup>
                  {cols.map((c) => {
                    const layoutCls = fenColLayoutClass(c);
                    if (c.width)
                      return (
                        <col
                          key={c.key}
                          className={layoutCls || undefined}
                          style={{ width: `${c.width}px` }}
                        />
                      );
                    if (c.widthPct)
                      return (
                        <col
                          key={c.key}
                          className={layoutCls || undefined}
                          style={{ width: `${c.widthPct}%` }}
                        />
                      );
                    return (
                      <col key={c.key} className={layoutCls || undefined} />
                    );
                  })}
                </colgroup>
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th
                        key={c.key}
                        className={
                          [
                            c.isImages ? "evt-th-is-images" : "",
                            fenColLayoutClass(c),
                          ]
                            .filter(Boolean)
                            .join(" ") || undefined
                        }
                        style={c.isImages ? { textAlign: "center" } : undefined}
                      >
                        <div className="th-content">
                          {c.isImages ? (
                            <span
                              className="evt-th-img-col"
                              data-sgc-tooltip="Imágenes"
                              aria-label="Imágenes"
                            >
                              <i className="bi bi-image" aria-hidden />
                            </span>
                          ) : (
                            <span>{c.label}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, idx) => {
                    const detalle = p?.detalle ?? {};
                    const imagenes = Array.isArray(p?.imagenes)
                      ? p.imagenes
                      : [];
                    return (
                      <tr key={p?.id ?? idx}>
                        {cols.map((c) => {
                          if (c.key === "__fuente") {
                            return (
                              <td
                                key={c.key}
                                className={[
                                  "evt-td-center evt-param-col-fuente",
                                  fenColLayoutClass(c),
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                data-label="Fuente"
                              >
                                {p?.referencia_bibliografica ?? "—"}
                              </td>
                            );
                          }
                          if (c.key === "__reporte") {
                            const reporteRaw =
                              detalle?.reporte_bibliografico ?? "";
                            const reporteText = String(reporteRaw ?? "").trim();
                            return (
                              <td
                                key={c.key}
                                className={[
                                  "evt-td-justify",
                                  fenColLayoutClass(c),
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                data-label="Reporte bibliográfico"
                              >
                                {reporteText ? (
                                  <div
                                    className="rich-text evt-html evt-html--tight"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        normalizeAdminRichText(reporteText),
                                    }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          }
                          if (c.key === "__imagenes") {
                            return (
                              <td
                                key={c.key}
                                className={[
                                  "evt-td-center evt-param-col-img",
                                  fenColLayoutClass(c),
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                data-label="Imágenes"
                              >
                                {imagenes.length > 0 ? (
                                  <button
                                    type="button"
                                    className="evt-img-btn evt-img-btn--param"
                                    onClick={() => openGallery(imagenes)}
                                    aria-label="Ver imágenes"
                                    data-sgc-tooltip="Ver imágenes"
                                  >
                                    <i className="bi bi-image" aria-hidden />
                                  </button>
                                ) : null}
                              </td>
                            );
                          }
                          const val = detalle?.[c.key];
                          const text =
                            val != null && String(val).trim() !== ""
                              ? String(val)
                              : "—";
                          const cls = [
                            fenColLayoutClass(c),
                            c.align === "justify"
                              ? "evt-td-justify"
                              : c.align === "center"
                                ? "evt-td-center"
                                : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td
                              key={c.key}
                              className={cls}
                              data-label={c.label}
                            >
                              {text === "—" ? (
                                "—"
                              ) : shouldRenderRich(val) ? (
                                <div
                                  className="rich-text evt-html evt-html--tight"
                                  dangerouslySetInnerHTML={{
                                    __html: normalizeAdminRichText(text),
                                  }}
                                />
                              ) : (
                                text
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="evt-empty">
              No hay datos para mostrar en {tipoSel}.
            </div>
          )}
        </section>
      </div>

      <EvtImagenesGaleriaModal
        open={modalOpen}
        images={modalImages}
        index={modalIndex}
        onClose={closeGallery}
        onPrev={prevImg}
        onNext={nextImg}
        onSelectIndex={setModalIndex}
      />
    </>
  );
}
